import { memo, useContext, useMemo, lazy, startTransition } from 'react';
import { determineLender, getObfuscatedCredit } from '@atc/wallet-payment-fns';
import { financingDisclaimers } from 'axl-config';
import { Box, LoadingSpinner } from 'reaxl';
import { LenderPayment } from 'reaxl-wallet';
import ConfigContext from '../../ConfigContext/ConfigContext.js';
import DisclaimerWithModalContent from '../../DisclaimerWithModalContent/DisclaimerWithModalContent.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
const LendingOptionsModal = /*#__PURE__*/lazy(() => import(/* webpackChunkName: "LendingOptionsModal" */'../../LendingOptionsModal/index.js'));
const OfferBreakdownModal = /*#__PURE__*/lazy(() => import(/* webpackChunkName: "OfferBreakdownModal" */'../../OfferBreakdownModal/index.js'));
const getCashOrFinancePrice = (isFinance, totalCashOut) => {
  if (isFinance) return 0;
  return Math.max(0, totalCashOut);
};

// JSDoc: see export at bottom of file
function OfferColumn(_ref) {
  let {
    lendingOptions,
    container,
    ctas,
    dealerFinancingCTA,
    disclaimerProps,
    dueAtSigningValue,
    financingCallToAction,
    handleFallbackPayment,
    handleLendingOptionsSelection,
    isLoading,
    isPennyPerfectPayment,
    listing,
    listingPaymentDetails,
    offerBreakdownProps,
    paymentCalculationDetails,
    renderTaxesAndFees,
    setShowLendingOptionsModal,
    showLendingOptionsModal,
    setShowOfferBreakdownModal,
    showOfferBreakdownModal,
    hasTaxesFeesModalOpen,
    totalCashOut,
    totalEquity,
    totalFinancing,
    totalTax,
    paymentErrorState,
    hasTaxesFetchError = false,
    incentivesProps = {},
    udsLocalStateValues = {},
    priceLabel = 'MSRP',
    priceDescription = ''
  } = _ref;
  const {
    digitalRetailingType,
    disableIncentives = false,
    enableLenderDisclaimerCTA = false,
    oemLabels = {},
    showLenderDisclaimer,
    showLenderName
  } = useContext(ConfigContext) ?? {};
  const {
    customRate,
    financeDownPayment,
    localInterestRate,
    localPrice,
    offerPrice,
    selectedCreditTier,
    selectedLeaseTerm,
    selectedLoanTerm,
    selectedMileage,
    activeTab,
    useCustomRate,
    vehicleCondition
  } = udsLocalStateValues;
  const {
    manufacturerOffer: appliedOffers
  } = offerBreakdownProps.getPricingBreakdown(activeTab);
  const {
    disclaimer: customOemDisclaimer
  } = oemLabels;
  const isFinance = activeTab === 'finance';
  const isLease = activeTab === 'lease';
  const isCash = activeTab === 'cash';
  const {
    leasePayment,
    adjustedCapCost,
    showSpecialAPR,
    payment: financePayment,
    lender
  } = disclaimerProps[activeTab] || {};
  const {
    owner = {}
  } = listing || {};
  const {
    privateSeller: isPSX
  } = owner;
  const lenderInterestRate = useCustomRate ? {
    custom: localInterestRate
  } : {
    [vehicleCondition]: localInterestRate
  };
  const lenderName = determineLender(customRate, lender);
  const isDRAccelerate = digitalRetailingType === 'accelerate';
  let price = totalFinancing <= 0 ? 0 : localPrice;
  const paymentsInfo = {
    isPennyPerfectPayment,
    totalEquity,
    ...(isFinance && {
      downPayment: financeDownPayment,
      loanTerm: selectedLoanTerm,
      interestRate: {
        ...lenderInterestRate
      },
      taxesFees: totalTax,
      showSpecialAPR
    }),
    ...(isLease && {
      downPayment: dueAtSigningValue,
      leaseTerm: selectedLeaseTerm,
      leaseMileageRange: selectedMileage
    })
  };
  const personalizedPayment = isLease ? leasePayment : financePayment;

  // Handler for onHide in Lending Options Modal
  const handleLendingOptionsModalHide = () => {
    startTransition(() => {
      setShowLendingOptionsModal(false);
    });
  };
  const handleOfferBreakdownModalHide = () => {
    startTransition(() => {
      setShowOfferBreakdownModal(false);
    });
  };

  // Determine which lending option is currently selected to preselect in modal
  const getLendingOptionIndex = useMemo(() => {
    if (typeof customRate === 'number' && customRate >= 0) return -1;
    const personalizedPayments = (paymentCalculationDetails || listingPaymentDetails)?.rates?.[getObfuscatedCredit(selectedCreditTier)]?.paymentOffers?.[selectedLoanTerm] ?? [];
    if (personalizedPayments.length) {
      const currentSelectionIndex = personalizedPayments.findIndex(option => option.offeredBy === lenderName && option[vehicleCondition] === localInterestRate);
      return currentSelectionIndex >= 0 ? currentSelectionIndex : 0;
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customRate, offerPrice, paymentCalculationDetails, listingPaymentDetails, selectedCreditTier, selectedLoanTerm, lenderName, localInterestRate]);
  const {
    onClick = () => {},
    handleSACACreditAppCTA
  } = dealerFinancingCTA || {};
  const updatedDealerFinancingCTA = {
    ...dealerFinancingCTA,
    onClick
  };
  if (handleSACACreditAppCTA) {
    updatedDealerFinancingCTA.onClick = () => {
      handleSACACreditAppCTA();
      startTransition(() => {
        setShowLendingOptionsModal(false);
      });
    };
  }

  /**
   * Renders the correct lender payment component based on the current tab and payment error state.
   *
   * @returns {React.ReactNode} The rendered lender payment component.
   */
  const renderLenderPayment = () => {
    let uiContext = 'withRedesign';
    if (isLoading && !hasTaxesFeesModalOpen) {
      return null;
    }
    if (paymentErrorState) {
      uiContext = 'error';
    }
    if (isCash || !paymentErrorState && isFinance && totalFinancing < 0) {
      price = getCashOrFinancePrice(isFinance, totalCashOut);
      return /*#__PURE__*/jsx(LenderPayment, {
        appliedOffers: appliedOffers,
        disableIncentives: disableIncentives,
        price: price,
        uiContext: "cash",
        callToAction: financingCallToAction,
        isPSX: isPSX,
        wrapperClassName: "margin-top-4"
      }, "cashPayment");
    }
    return /*#__PURE__*/jsx(LenderPayment, {
      appliedOffers: appliedOffers,
      disableIncentives: disableIncentives,
      isPSX: isPSX,
      paymentsInfo: paymentsInfo,
      price: price,
      dealType: isLease ? 'lease' : undefined,
      enableLenderDisclaimerCTA: enableLenderDisclaimerCTA,
      interestRate: isFinance ? localInterestRate : undefined,
      vehicleCondition: vehicleCondition,
      uiContext: uiContext,
      lender: isFinance ? lenderName : undefined,
      showLenderName: showLenderName,
      personalizedPayment: personalizedPayment,
      showLenderDisclaimer: isCash ? false : showLenderDisclaimer,
      callToAction: financingCallToAction,
      handleFallbackPayment: isFinance ? handleFallbackPayment : undefined,
      wrapperClassName: "margin-top-4"
    }, "lendingPayment");
  };
  return /*#__PURE__*/jsxs(Fragment, {
    children: [isFinance && !isDRAccelerate && showLendingOptionsModal && /*#__PURE__*/jsx(LendingOptionsModal, {
      container: container,
      onSubmit: handleLendingOptionsSelection,
      onHide: handleLendingOptionsModalHide,
      options: lendingOptions,
      disclaimer: showLenderDisclaimer ? financingDisclaimers.defaultLenderDisclaimer : undefined,
      initialCustomRate: customRate?.toString() ?? '',
      initialSelection: getLendingOptionIndex,
      customPaymentDetails: {
        price: parseInt(localPrice, 10),
        loanTerm: Number.parseInt(selectedLoanTerm, 10),
        downPayment: financeDownPayment,
        totalEquity,
        taxesFees: totalTax
      },
      secondaryCTA: updatedDealerFinancingCTA
    }, "lendingOptionsModal"), showOfferBreakdownModal && /*#__PURE__*/jsx(OfferBreakdownModal, {
      container: container,
      isVisible: showOfferBreakdownModal,
      onHide: handleOfferBreakdownModalHide,
      tabName: activeTab,
      renderTaxesAndFees: renderTaxesAndFees,
      dueAtSigningValue: isLease ? dueAtSigningValue : undefined,
      adjustedCapCost: isLease ? adjustedCapCost : undefined,
      downPayment: isFinance ? financeDownPayment : undefined,
      totalFinancing: isFinance ? totalFinancing : undefined,
      totalCashOut: isCash ? totalCashOut : undefined,
      paymentErrorState: paymentErrorState,
      disclaimerPaymentProps: disclaimerProps[activeTab],
      offerBreakdownProps: offerBreakdownProps,
      price: price,
      paymentsInfo: paymentsInfo,
      personalizedPayment: personalizedPayment,
      vehicleCondition: vehicleCondition,
      isLoading: isLoading,
      hasTaxesFetchError: hasTaxesFetchError,
      priceLabel: priceLabel,
      priceDescription: priceDescription,
      ...incentivesProps
    }), renderLenderPayment(), isLoading && !hasTaxesFeesModalOpen && /*#__PURE__*/jsx(Box, {
      className: "margin-bottom-5 margin-top-4",
      borderColor: "neutral-secondary",
      backgroundColor: "transparent",
      children: /*#__PURE__*/jsx(LoadingSpinner, {
        className: "margin-vertical-6"
      })
    }), customOemDisclaimer && /*#__PURE__*/jsx(DisclaimerWithModalContent, {
      block: true,
      container: container,
      modalText: customOemDisclaimer,
      modalTitle: "Disclaimer",
      modalTriggerText: "View Disclaimer",
      "data-cmp": "custom-oem-disclaimer"
    })]
  });
}
/**
 *  Renders estimated finance/lease/cash payment, Offer Breakdown, CTAs, and disclaimer.
 *  Renders as the right column on non-mobile layouts and when the `isSingleColumn` config item is set to false.
 *
 * @component
 */
var OfferColumn$1 = /*#__PURE__*/memo(OfferColumn);
export { OfferColumn$1 as default };
